import React, { useState } from 'react'
import { CheckCircle, XCircle, BookOpen, AlertCircle, Maximize2, Minimize2 } from 'lucide-react'
import KaTeXRenderer from '../common/KaTeXRenderer'
import AIButton from '../ai/AIButton'

interface MultipleChoiceSolutionDisplayProps {
  question: any
  userAnswer: any
  analysis: any
}

const MultipleChoiceSolutionDisplay: React.FC<MultipleChoiceSolutionDisplayProps> = ({
  question,
  userAnswer,
  analysis
}) => {
  const [imageExpanded, setImageExpanded] = useState(false)
  const rawOptions = question.lua_chon || question.luaChon || []
  // Đề MỚI: mỗi phương án là {id, content} — option_id ổn định, KHÔNG phụ thuộc vị trí/chữ cái.
  // Đề CŨ: mảng string thuần — vẫn dùng cơ chế letter/index như trước (tương thích ngược).
  const isNewFormat = rawOptions.length > 0 && typeof rawOptions[0] === 'object' && rawOptions[0] !== null && rawOptions[0].id != null
  const options: string[] = isNewFormat ? rawOptions.map((o: any) => o.content ?? '') : rawOptions
  const correctAnswer = question.dap_an_dung || analysis.correctAnswer

  // Debug logging
  console.log('🔍 MultipleChoiceSolutionDisplay - Debug info:', {
    questionId: question.id,
    questionType: question.type || (question as any).loai,
    dap_an_dung: question.dap_an_dung,
    analysisCorrectAnswer: analysis.correctAnswer,
    finalCorrectAnswer: correctAnswer,
    userAnswer,
    lua_chon: question.lua_chon
  })

  // Convert correct answer to index if it's a letter
  const getCorrectIndex = () => {
    console.log(`🔍 MultipleChoiceSolutionDisplay - getCorrectIndex:`, {
      correctAnswer,
      type: typeof correctAnswer,
      questionId: question.id,
      questionType: question.type || (question as any).loai
    })

    // Đề MỚI: correctAnswer là option_id — tra trực tiếp theo id, KHÔNG qua chữ cái.
    if (isNewFormat && typeof correctAnswer === 'string') {
      const idx = rawOptions.findIndex((o: any) => o && o.id === correctAnswer)
      if (idx >= 0) return idx
    }

    // Handle string answers (A, B, C, D or "0", "1", "2", "3")
    if (typeof correctAnswer === 'string') {
      const letter = correctAnswer.toUpperCase().trim()
      console.log(`🔍 Processing string answer: "${letter}"`)
      
      // Direct letter mapping
      if (letter === 'A') return 0
      if (letter === 'B') return 1
      if (letter === 'C') return 2
      if (letter === 'D') return 3
      
      // Try to parse as number string ("0", "1", "2", "3")
      const num = parseInt(letter)
      if (!isNaN(num) && num >= 0 && num <= 3) {
        console.log(`🔍 Parsed string as number: ${num}`)
        return num
      }
    }
    
    // Handle number answers (0, 1, 2, 3)
    if (typeof correctAnswer === 'number') {
      console.log(`🔍 Processing number answer: ${correctAnswer}`)
      if (correctAnswer >= 0 && correctAnswer <= 3) {
        return correctAnswer
      }
    }
    
    // Handle boolean (rare case)
    if (typeof correctAnswer === 'boolean') {
      console.log(`🔍 Processing boolean answer: ${correctAnswer}`)
      return correctAnswer ? 1 : 0
    }
    
    // Try to extract from question data if correctAnswer is still invalid
    if (!correctAnswer || correctAnswer === -1) {
      console.log(`🔍 Trying to find correct answer from question data...`)
      
      // Check if there's a marked correct answer in the question
      if ((question as any).dap_an_dung_index !== undefined) {
        console.log(`✅ Found dap_an_dung_index: ${(question as any).dap_an_dung_index}`)
        return (question as any).dap_an_dung_index
      }
      
      if ((question as any).dap_an_dung_letter) {
        const letter = (question as any).dap_an_dung_letter.toUpperCase()
        console.log(`✅ Found dap_an_dung_letter: ${letter}`)
        if (letter === 'A') return 0
        if (letter === 'B') return 1
        if (letter === 'C') return 2
        if (letter === 'D') return 3
      }
      
      // Try to find from interactive question structure
      if ((question as any).correctAnswer !== undefined) {
        const interactiveCorrect = (question as any).correctAnswer
        console.log(`✅ Found interactive correctAnswer: ${interactiveCorrect}`)
        
        if (typeof interactiveCorrect === 'number' && interactiveCorrect >= 0 && interactiveCorrect <= 3) {
          return interactiveCorrect
        }
        
        if (typeof interactiveCorrect === 'string') {
          const letter = interactiveCorrect.toUpperCase().trim()
          if (letter === 'A') return 0
          if (letter === 'B') return 1
          if (letter === 'C') return 2
          if (letter === 'D') return 3
        }
      }
    }
    
    console.log(`❌ Could not determine correct index for: ${correctAnswer}`)
    return -1
  }

  // Convert user answer to index
  const getUserIndex = () => {
    // Đề MỚI: userAnswer là option_id đã chọn — tra trực tiếp theo id.
    if (isNewFormat && typeof userAnswer === 'string') {
      const idx = rawOptions.findIndex((o: any) => o && o.id === userAnswer)
      if (idx >= 0) return idx
    }
    if (typeof userAnswer === 'string') {
      const letter = userAnswer.toUpperCase()
      if (letter === 'A') return 0
      if (letter === 'B') return 1
      if (letter === 'C') return 2
      if (letter === 'D') return 3
    }
    if (typeof userAnswer === 'number') {
      return userAnswer
    }
    return -1
  }

  const correctIndex = getCorrectIndex()
  const userIndex = getUserIndex()
  const hasAnswer = userIndex !== -1

  // Enhanced fallback: If we still don't have a correct index, try to infer from analysis
  let finalCorrectIndex = correctIndex
  if (finalCorrectIndex === -1 && analysis && analysis.correctAnswer !== undefined) {
    console.log(`🔍 Trying analysis.correctAnswer as fallback:`, analysis.correctAnswer)
    if (typeof analysis.correctAnswer === 'string') {
      const letter = analysis.correctAnswer.toUpperCase()
      if (letter === 'A') finalCorrectIndex = 0
      else if (letter === 'B') finalCorrectIndex = 1
      else if (letter === 'C') finalCorrectIndex = 2
      else if (letter === 'D') finalCorrectIndex = 3
    } else if (typeof analysis.correctAnswer === 'number' && analysis.correctAnswer >= 0 && analysis.correctAnswer <= 3) {
      finalCorrectIndex = analysis.correctAnswer
    }
    console.log(`🔍 Final correct index after fallback: ${finalCorrectIndex}`)
  }

  // Get the text of the correct answer for display
  const getCorrectAnswerText = () => {
    // If we have a valid index, get the option text
    if (finalCorrectIndex >= 0 && finalCorrectIndex < options.length) {
      return options[finalCorrectIndex]
    }
    
    // If correctAnswer is already text, use it directly
    if (typeof correctAnswer === 'string' && correctAnswer.trim()) {
      return correctAnswer.trim()
    }
    
    // If analysis.correctAnswer is text, use it
    if (analysis && typeof analysis.correctAnswer === 'string' && analysis.correctAnswer.trim()) {
      return analysis.correctAnswer.trim()
    }
    
    return null
  }

  const correctAnswerText = getCorrectAnswerText()

  return (
    <div className="mt-2 space-y-2">
      {/* Question Content */}
      <div className="bg-gray-50 rounded p-3 relative group">
        <div className="text-base text-gray-700">
          <KaTeXRenderer content={question.cau_hoi || question.cauHoi || ''} moveImagesToEnd />
        </div>
        
        {/* AI Button - Hiển thị ở chế độ xem lại */}
        <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity">
          <AIButton
            content={question}
            contentType="quiz"
            size="sm"
            variant="outline"
            title="Giải thích câu hỏi trắc nghiệm"
            className="bg-white border-blue-300 text-blue-600 hover:bg-blue-50"
            questionId={question.id}
            useDatabase={true}
          />
        </div>
        
        {/* Question Image with 50% screen mode */}
        {(question.hinh_anh || question.questionImage) && (
          <div className="mt-2 w-full overflow-hidden">
            <div className="relative w-full max-w-full">
              <img
                src={question.hinh_anh || question.questionImage}
                alt="Hình ảnh câu hỏi"
                className={`rounded border border-gray-200 transition-all duration-300 ${
                  imageExpanded 
                    ? 'fixed top-0 left-0 w-1/2 h-screen object-contain bg-black bg-opacity-90 z-50 cursor-pointer' 
                    : 'w-full h-auto max-h-[180px] sm:max-h-[200px] md:max-h-[220px] cursor-pointer hover:opacity-80 mx-auto block object-contain'
                }`}
                style={{ maxWidth: imageExpanded ? '50%' : '100%' }}
                onClick={() => setImageExpanded(!imageExpanded)}
                onError={(e) => {
                  const target = e.currentTarget as HTMLImageElement
                  target.style.display = 'none'
                }}
              />
              
              {/* Image controls */}
              <button
                onClick={() => setImageExpanded(!imageExpanded)}
                className="absolute top-2 right-2 bg-white bg-opacity-80 hover:bg-opacity-100 rounded-full p-2 shadow-md transition-all"
                title={imageExpanded ? 'Thu nhỏ ảnh' : 'Phóng to ảnh (50% màn hình)'}
              >
                {imageExpanded ? (
                  <Minimize2 className="w-4 h-4 text-gray-700" />
                ) : (
                  <Maximize2 className="w-4 h-4 text-gray-700" />
                )}
              </button>
              
              {imageExpanded && (
                <div className="fixed top-0 right-0 w-1/2 h-screen bg-white overflow-y-auto z-50 p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-xl font-semibold">Chi tiết câu hỏi</h3>
                    <button
                      onClick={() => setImageExpanded(false)}
                      className="bg-gray-200 hover:bg-gray-300 rounded-full p-2"
                    >
                      <XCircle className="w-5 h-5" />
                    </button>
                  </div>
                  
                  <div className="space-y-4">
                    <div>
                      <h4 className="font-medium mb-2">Câu hỏi:</h4>
                      <div className="bg-gray-50 p-3 rounded">
                        <KaTeXRenderer content={question.cau_hoi || question.cauHoi || ''} moveImagesToEnd />
                      </div>
                    </div>
                    
                    <div>
                      <h4 className="font-medium mb-2">Kết quả của bạn:</h4>
                      <div className={`p-3 rounded ${
                        analysis.isCorrect ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'
                      }`}>
                        {analysis.isCorrect ? 'Chính xác!' : 'Chưa chính xác'} - {analysis.points} điểm
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Answer Options - Simplified with color marking */}
      <div className="space-y-1.5">
        {options.map((option: string, index: number) => {
          const isCorrect = index === finalCorrectIndex
          const isUserChoice = index === userIndex
          const letter = String.fromCharCode(65 + index) // A, B, C, D

          return (
            <div
              key={index}
              className={`border rounded p-2 ${
                isCorrect && isUserChoice
                  ? 'border-green-500 bg-green-100' 
                  : isCorrect 
                    ? 'border-green-500 bg-green-50'
                    : isUserChoice 
                      ? 'border-red-500 bg-red-100'
                      : 'border-gray-200 bg-white'
              }`}
            >
              <div className="flex items-start gap-2">
                <span className="font-medium text-gray-700 min-w-[24px]">
                  {letter}.
                </span>
                <div className="flex-1 text-base text-gray-700">
                  <KaTeXRenderer content={option} />
                </div>
                {isCorrect && (
                  <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0" />
                )}
                {isUserChoice && !isCorrect && (
                  <XCircle className="w-4 h-4 text-red-600 flex-shrink-0" />
                )}
              </div>
            </div>
          )
        })}
      </div>

      {/* Detailed Solution */}
      {(analysis.explanation || question.loi_giai) && (
        <div className="bg-blue-50 border border-blue-200 rounded p-3">
          <div className="flex items-center gap-2 mb-2">
            <BookOpen className="w-4 h-4 text-blue-600" />
            <span className="font-semibold text-blue-800 text-base">Lời giải chi tiết:</span>
          </div>
          <div className="text-base text-blue-700">
            <KaTeXRenderer content={analysis.explanation || question.loi_giai} />
          </div>
          
          {/* Solution Image */}
          {(question.hinh_anh_giai_thich || question.solution_image_link) && (
            <div className="mt-2 w-full overflow-hidden">
              <img
                src={question.hinh_anh_giai_thich || question.solution_image_link}
                alt="Hình ảnh lời giải"
                className="w-full h-auto max-h-[180px] sm:max-h-[200px] md:max-h-[220px] rounded border border-gray-200 mx-auto block object-contain"
                style={{ maxWidth: '100%', width: 'auto' }}
                onError={(e) => {
                  const target = e.currentTarget as HTMLImageElement
                  target.style.display = 'none'
                }}
              />
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default MultipleChoiceSolutionDisplay